import { getDefaultHeaderHeight } from '@react-navigation/elements'
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import React, { useMemo, useRef } from 'react'
import {
  Dimensions,
  FlatList,
  StatusBar,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { CommunityChatResource } from '~api/types'
import { actions, useNewChatContext } from '~components/new-chat/state'
import ChatSection from '~components/ChatSection'
import { Item } from '~components/header/HeaderItems'
import Loading from '~components/Loading'
import { initialChatSection } from '~services/chat'
import { Theme } from '~theme'
import { useTheme } from '~theme/ThemeManager'
import { ChatSection as ChatSectionType, ChatTemplate, IChatLayout } from '~types'
import { useAxios } from '~utils/fetch'

export default function ChattaCommunityTemplateScreen(): React.JSX.Element {
  const { theme } = useTheme()
  const styles = getStyles(theme)
  const dimensions = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const headerHeight = React.useMemo(
    () => getDefaultHeaderHeight(dimensions, false, insets.top),
    [dimensions, insets],
  )
  const list = useRef<FlatList>()
  const { templateId } = useLocalSearchParams<{
    templateId: string
    type: CommunityChatResource['type']
  }>()
  const router = useRouter()
  const navigation = useNavigation()
  const [state, dispatch] = useNewChatContext()

  const [{ data: template, loading, error }] = useAxios(`/api/v1/community/chats/${templateId}`)

  if (error) {
    router.canGoBack() && router.back()
  }

  const doc: ChatTemplate | undefined = (() => {
    if (template === undefined) {
      return undefined
    }

    const doc: ChatTemplate = {
      id: template.data.id,
      name: template.data.name ?? '',
      type: template.data.type ?? '',
      ...template.data.document,
    }

    const layout: IChatLayout = {
      rows: doc?.layout?.rows ?? 1,
      columns: doc?.layout?.columns ?? 1,
    }
    const sections: Record<number, ChatSectionType> = {
      ...Array.from(Array(layout.rows * layout.columns).keys()).map((index) => ({
        ...initialChatSection,
        ...(doc?.sections[index] ?? {}),
      })),
    }

    const chat: ChatTemplate = {
      ...doc,
      layout,
      sections,
    }

    return chat
  })()

  const data = Array.from(Array((doc?.layout?.rows || 1) * (doc?.layout?.columns || 1)).keys()).map(
    (index) => {
      return {
        index,
        key: `section_${index}`,
      }
    },
  )

  const small = Dimensions.get('window').width < 480 || Dimensions.get('window').height < 480

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: small ? 'Template' : (doc?.name ?? 'Template'),
      headerRight: () => (
        <>
          <Item
            title="Report"
            icon={['far', 'flag']}
            onPress={() => router.navigate(`./${templateId}/report`)}
          />
          <Item
            title="Use this template"
            icon={['far', 'check']}
            onPress={() => {
              if (doc) {
                dispatch(actions.setTemplate(doc))
                router.push(
                  state.showFolderSelection
                    ? `/folders/new-chat/details`
                    : `/folders/${state.class.id}/new-chat/details`,
                )
              }
            }}
            disabled={doc === undefined}
          />
        </>
      ),
    })
  }, [
    navigation,
    theme,
    styles,
    small,
    doc,
    templateId,
    router,
    dispatch,
    state.showFolderSelection,
    state.class.id,
  ])

  const displayLayout: IChatLayout = small
    ? {
        rows: 1,
        columns: 1,
      }
    : {
        rows: doc?.layout?.rows ?? 1,
        columns: doc?.layout?.columns ?? 1,
      }

  const chatSectionOptions = useMemo(() => {
    const availableHeight =
      dimensions.height -
      (StatusBar.currentHeight || 0) -
      20 -
      (headerHeight || 0) -
      insets.bottom -
      insets.top

    const heightEach = Math.floor(availableHeight / displayLayout.rows)
    const heightMin = 250

    const height = Math.max(heightEach, heightMin)

    return {
      scrollEnabled: heightEach < heightMin,
      style: {
        flexBasis: 0,
        minHeight: height,
        height,
      },
    }
  }, [dimensions, headerHeight, insets.top, insets.bottom, displayLayout.rows])

  const renderItem = ({ item }) => (
    <ChatSection
      style={chatSectionOptions.style}
      key={item.key}
      index={item.index}
      template={doc?.sections[item.index]}
    />
  )

  if (loading || doc === undefined) {
    return <Loading />
  }

  return (
    <View style={styles.container}>
      <FlatList
        ref={(c) => (list.current = c)}
        key={`chat_${displayLayout.rows}_${displayLayout.columns}`}
        style={styles.chat}
        scrollEnabled={small || chatSectionOptions.scrollEnabled}
        contentContainerStyle={styles.chatContainer}
        numColumns={displayLayout.columns}
        snapToAlignment="start"
        snapToInterval={chatSectionOptions.style.height}
        decelerationRate="fast"
        pagingEnabled
        data={data}
        renderItem={renderItem}
        getItemLayout={(data, index) => ({
          length: chatSectionOptions.style.height,
          offset: chatSectionOptions.style.height * index,
          index,
        })}
      />
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    chat: {
      flex: 1,
    },
    chatContainer: {
      padding: 10,
      flexGrow: 1,
    },
    navIconsContainer: {
      flexDirection: 'row',
      paddingEnd: 10,
    },
    navIconsButton: {
      marginLeft: 10,
      marginBottom: 0,
    },
    navIconsButtonReport: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
    navTextStyleReport: {
      color: theme[800],
    },
    navIcons: {
      marginRight:
        Dimensions.get('window').width < 480 || Dimensions.get('window').height < 480 ? 0 : 10,
    },
  })
